import type { IntelData } from "../translate";
import type {
  CommandCenterAgent,
  CommandCenterDirection,
  CommandCenterOpenTradeLike,
  CommandCenterRowV2,
  CommandCenterSession,
  CommandCenterSignalLike,
  CommandCenterStrategyState,
} from "./types";

const AGENTS: readonly CommandCenterAgent[] = ["orb", "fbd", "mr"];

interface BuildCommandCenterRowsArgs {
  symbols: readonly string[];
  session: CommandCenterSession;
  intelMap: Record<string, IntelData | undefined>;
  signals: CommandCenterSignalLike[];
  openTrades: CommandCenterOpenTradeLike[];
}

function rootSymbol(symbol: string | undefined): string {
  return (symbol || "").replace(/[A-Z]\d+$/, "").toUpperCase();
}

function agent(value: string | undefined): CommandCenterAgent | null {
  const normalized = value?.toLowerCase();
  return normalized === "orb" || normalized === "fbd" || normalized === "mr" ? normalized : null;
}

function direction(value: string | undefined): CommandCenterDirection | null {
  const normalized = value?.toLowerCase();
  if (normalized === "long" || normalized === "buy" || normalized === "buying" || normalized === "bullish") return "long";
  if (normalized === "short" || normalized === "sell" || normalized === "selling" || normalized === "bearish") return "short";
  return null;
}

function timestamp(value: { created_at?: number; opened_at?: number }): number {
  return value.opened_at ?? value.created_at ?? 0;
}

function sessionMatches(rowSession: string | undefined, session: CommandCenterSession): boolean {
  return session === "all" || rowSession?.toLowerCase() === session;
}

function riskReward(directionValue: CommandCenterDirection | null, entry: number | null, stopLoss: number | null, tp1: number | null): number | null {
  if (!directionValue || typeof entry !== "number" || typeof stopLoss !== "number" || typeof tp1 !== "number") return null;
  const risk = directionValue === "short" ? stopLoss - entry : entry - stopLoss;
  const reward = directionValue === "short" ? entry - tp1 : tp1 - entry;
  if (risk <= 0 || reward <= 0) return null;
  return Number((reward / risk).toFixed(2));
}

function strategyFromSignal(signal: CommandCenterSignalLike | undefined): CommandCenterStrategyState {
  if (!signal) return emptyStrategy();
  const normalizedAgent = agent(signal.agent);
  const normalizedDirection = direction(signal.direction);
  const entry = signal.entry ?? null;
  const stopLoss = signal.stop_loss ?? null;
  const tp1 = signal.tp1 ?? null;
  return {
    state: "new_signal",
    agent: normalizedAgent,
    direction: normalizedDirection,
    entry,
    stopLoss,
    tp1,
    tp2: signal.tp2 ?? null,
    rrTp1: riskReward(normalizedDirection, entry, stopLoss, tp1),
    signalId: signal.signal_id ?? signal._id ?? null,
    createdAt: signal.created_at ?? null,
    outcome: signal.outcome ?? null,
    score: signal.score ?? null,
  };
}

function strategyFromTrade(trade: CommandCenterOpenTradeLike | undefined): CommandCenterStrategyState | null {
  if (!trade) return null;
  const normalizedAgent = agent(trade.agent);
  const normalizedDirection = direction(trade.direction);
  const entry = trade.entry_price ?? null;
  const stopLoss = trade.stop_loss ?? null;
  const tp1 = trade.tp1 ?? null;
  return {
    state: "open_trade",
    agent: normalizedAgent,
    direction: normalizedDirection,
    entry,
    stopLoss,
    tp1,
    tp2: trade.tp2 ?? null,
    rrTp1: riskReward(normalizedDirection, entry, stopLoss, tp1),
    signalId: null,
    createdAt: trade.opened_at ?? trade.created_at ?? null,
    outcome: "OPEN",
    score: null,
  };
}

function emptyStrategy(): CommandCenterStrategyState {
  return {
    state: "no_signal",
    agent: null,
    direction: null,
    entry: null,
    stopLoss: null,
    tp1: null,
    tp2: null,
    rrTp1: null,
    signalId: null,
    createdAt: null,
    outcome: null,
    score: null,
  };
}

function latestSignalForAgent(signals: CommandCenterSignalLike[], symbol: string, session: CommandCenterSession, targetAgent: CommandCenterAgent): CommandCenterSignalLike | undefined {
  return signals
    .filter((signal) => agent(signal.agent) === targetAgent)
    .filter((signal) => rootSymbol(signal.symbol) === symbol)
    .filter((signal) => sessionMatches(signal.session, session))
    .sort((a, b) => timestamp(b) - timestamp(a))[0];
}

function latestOpenTrade(openTrades: CommandCenterOpenTradeLike[], symbol: string, session: CommandCenterSession): CommandCenterOpenTradeLike | undefined {
  return openTrades
    .filter((trade) => trade.status === "open")
    .filter((trade) => rootSymbol(trade.symbol) === symbol)
    .filter((trade) => sessionMatches(trade.session, session))
    .sort((a, b) => timestamp(b) - timestamp(a))[0];
}

function latestStrategySignal(strategies: CommandCenterStrategyState[]): CommandCenterStrategyState {
  return strategies
    .filter((strategy) => strategy.state === "new_signal")
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0] ?? emptyStrategy();
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function asNumberRecord(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object") return {};
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === "number" && Number.isFinite(raw)) result[key] = raw;
  }
  return result;
}

function isStaleStatus(status: string | undefined): boolean {
  return status === "stale" || status === "missing";
}

function sideFromBias(...values: Array<string | null | undefined>): CommandCenterDirection | null {
  for (const value of values) {
    const normalized = value?.toLowerCase() ?? "";
    if (normalized.includes("bull") || normalized.includes("buy") || normalized.includes("long")) return "long";
    if (normalized.includes("bear") || normalized.includes("sell") || normalized.includes("short")) return "short";
  }
  return null;
}

function sideFromActiveEntryAction(value: string | null | undefined): CommandCenterDirection | null {
  const normalized = value?.toUpperCase() ?? "";
  if (normalized === "ENTER_LONG") return "long";
  if (normalized === "ENTER_SHORT") return "short";
  return null;
}

function isWaitAction(value: string | null | undefined): boolean {
  const normalized = value?.toUpperCase() ?? "";
  return normalized.startsWith("WAIT") || normalized.includes("NO_TRADE") || normalized.includes("STAND_DOWN");
}

function buildBlockers(data: IntelData | undefined, strategy: CommandCenterStrategyState): string[] {
  const blockers: string[] = [];
  if (!data || data.data_status === "missing") blockers.push("missing live data");
  if (data?.data_status === "stale") blockers.push("snapshot stale");
  if (isStaleStatus(data?.current_price_status)) blockers.push(`price ${data?.current_price_status}`);
  if (isStaleStatus(data?.order_flow_status)) blockers.push(`order flow ${data?.order_flow_status}`);
  if (isStaleStatus(data?.volume_flow_status)) blockers.push(`volume flow ${data?.volume_flow_status}`);
  if (strategy.agent === "fbd" && strategy.direction === "short") blockers.push("FBD is long-only");
  if (strategy.agent === "mr" && strategy.direction === "long") blockers.push("MR is short-only");
  blockers.push(...asStringArray(data?.entry_blockers));
  return Array.from(new Set(blockers));
}

function buildCoachFlow(data: IntelData | undefined, strategy: CommandCenterStrategyState) {
  const blockers = buildBlockers(data, strategy);
  const flowSide = sideFromBias(
    data?.current_bias,
    data?.flow_state_bias,
    data?.order_book_bias,
    data?.volume_flow_bias,
    data?.institutional_direction,
  );
  const activeEntrySide = sideFromActiveEntryAction(data?.entry_action);
  const hasExplicitWait = isWaitAction(data?.entry_action);

  let verdict: CommandCenterRowV2["coachFlow"]["verdict"] = "wait";
  if (blockers.length > 0) verdict = "blocked";
  else if (!strategy.direction) verdict = "wait";
  else if (activeEntrySide && activeEntrySide === strategy.direction) verdict = activeEntrySide === "long" ? "confirm_long" : "confirm_short";
  else if (activeEntrySide && activeEntrySide !== strategy.direction) verdict = strategy.state === "open_trade" ? "protect_exit" : "conflict";
  else if (strategy.state === "open_trade" && flowSide && flowSide !== strategy.direction) verdict = "protect_exit";
  else if (hasExplicitWait) verdict = "wait";
  else if (!flowSide) verdict = "wait";
  else if (flowSide === strategy.direction) verdict = flowSide === "long" ? "confirm_long" : "confirm_short";
  else verdict = strategy.state === "open_trade" ? "protect_exit" : "conflict";

  return {
    verdict,
    entryAction: data?.entry_action ?? null,
    entryReason: data?.entry_reason ?? null,
    flowState: data?.flow_state ?? null,
    flowBias: data?.current_bias ?? data?.flow_state_bias ?? null,
    orderBookBias: data?.order_book_bias ?? null,
    volumeFlowBias: data?.volume_flow_bias ?? null,
    confidence: typeof data?.flow_state_confidence === "number" ? data.flow_state_confidence : null,
    trigger: data?.confirmation ?? data?.entry_plan?.confirmation ?? null,
    invalidation: data?.invalidation ?? data?.entry_plan?.invalidation ?? null,
    blockers,
  };
}

function priceStatus(data: IntelData | undefined): CommandCenterRowV2["price"]["status"] {
  const status = data?.current_price_status;
  if (status === "fresh" || status === "stale" || status === "missing") return status;
  if (typeof data?.current_price === "number") return data.data_status === "stale" ? "stale" : "fresh";
  return "missing";
}

export function buildCommandCenterRowsV2(args: BuildCommandCenterRowsArgs): CommandCenterRowV2[] {
  return args.symbols.map((symbol) => {
    const data = args.intelMap[symbol];
    const strategySignals = AGENTS.map((targetAgent) => strategyFromSignal(latestSignalForAgent(args.signals, symbol, args.session, targetAgent)));
    const openTradeStrategy = strategyFromTrade(latestOpenTrade(args.openTrades, symbol, args.session));
    const signalStrategy = latestStrategySignal(strategySignals);
    const strategy = openTradeStrategy ?? signalStrategy;

    return {
      symbol,
      session: args.session,
      price: {
        value: typeof data?.current_price === "number" ? data.current_price : null,
        status: priceStatus(data),
        ageSec: typeof data?.current_price_age_sec === "number" ? data.current_price_age_sec : null,
        source: data?.current_price_source ?? null,
      },
      strategy,
      strategySignals,
      coachFlow: buildCoachFlow(data, strategy),
      marketFlowStack: data?.market_flow_stack && typeof data.market_flow_stack === "object" ? data.market_flow_stack : null,
      proof: {
        orderFlow: {
          status: data?.order_flow_status ?? null,
          source: data?.order_flow_source ?? null,
          ageSec: typeof data?.order_flow_age_sec === "number" ? data.order_flow_age_sec : null,
          institutionalDirection: data?.institutional_direction ?? null,
          institutionalScore: typeof data?.institutional_score === "number" ? data.institutional_score : null,
          absorptionBid: data?.absorption_bid ?? null,
          absorptionAsk: data?.absorption_ask ?? null,
          icebergBid: data?.iceberg_bid ?? null,
          icebergAsk: data?.iceberg_ask ?? null,
          spoofBidRisk: typeof data?.spoof_bid_risk === "number" ? data.spoof_bid_risk : null,
          spoofAskRisk: typeof data?.spoof_ask_risk === "number" ? data.spoof_ask_risk : null,
        },
        volumeFlow: {
          status: data?.volume_flow_status ?? null,
          source: data?.volume_flow_source ?? null,
          ageSec: typeof data?.volume_flow_age_sec === "number" ? data.volume_flow_age_sec : null,
          cmf20: typeof data?.cmf_20 === "number" ? data.cmf_20 : null,
          mfi14: typeof data?.mfi_14 === "number" ? data.mfi_14 : null,
          fbdScore: typeof data?.fbd_flow_score === "number" ? data.fbd_flow_score : null,
          mrScore: typeof data?.mr_flow_score === "number" ? data.mr_flow_score : null,
        },
        tapeAccumulation: data?.tape_accumulation_windows && typeof data.tape_accumulation_windows === "object" ? data.tape_accumulation_windows : null,
        tapeAccumulationSource: data?.tape_accumulation_source ?? null,
        delta: asNumberRecord(data?.delta),
        cumulativeTape: data?.cumulative_tape && typeof data.cumulative_tape === "object" ? data.cumulative_tape as Record<string, unknown> : null,
        dataWarnings: asStringArray(data?.data_warnings),
      },
      rawIntel: data,
    };
  });
}
